import { useState } from 'react';
import { supabase } from '../lib/supabase';

export function useQuoteFormSubmission(onSuccess?: () => void) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (formData: any, user: any) => {
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Calculate age
      const age = calculateAge(formData.personalInfo.dateOfBirth);
      
      // Validate age (must be at least 17 for car insurance)
      if (age < 17) {
        throw new Error('You must be at least 17 years old to get car insurance');
      }

      // Format dates
      const now = new Date();
      const expiryDate = new Date(now.getTime() + parseInt(formData.biddingDuration) * 60 * 60 * 1000);

      // Prepare request data
      const requestData = {
        // Status fields
        status: 'new',
        bid_status: 'awaiting_bids',
        reference_number: generateReferenceNumber(),
        start_date: formData.startDate,

        // Personal Information
        name: `${formData.personalInfo.firstName} ${formData.personalInfo.surname}`,
        title: formData.personalInfo.title,
        first_name: formData.personalInfo.firstName,
        surname: formData.personalInfo.surname,
        email: user.email,
        gender: formData.personalInfo.gender,
        date_of_birth: formData.personalInfo.dateOfBirth,
        phone: `${formData.personalInfo.countryCode}${formData.personalInfo.phone}`,
        age: age,
        address: formData.personalInfo.address,
        region: formData.personalInfo.region,
        
        // Vehicle Information
        car_make: formData.vehicleInfo.make,
        car_model: formData.vehicleInfo.model,
        car_year: parseInt(formData.vehicleInfo.year),
        is_owner: formData.vehicleInfo.ownership.isOwner === 'Yes',
        is_keeper: formData.vehicleInfo.ownership.isKeeper === 'Yes',
        purchase_date: formData.vehicleInfo.ownership.notPurchased ? null : formData.vehicleInfo.ownership.purchaseDate,
        parking_location: formData.vehicleInfo.ownership.parkingLocation,
        transmission: formData.vehicleInfo.transmission,
        drive_position: formData.vehicleInfo.drivePosition,
        seats: parseInt(formData.vehicleInfo.seats),
        alarm_type: formData.vehicleInfo.alarm,
        has_tracker: formData.vehicleInfo.tracker === 'Yes',
        car_value: parseFloat(formData.vehicleInfo.value),
        registration: formData.vehicleInfo.registration,
        mileage: parseInt(formData.vehicleInfo.mileage),
        is_modified: formData.vehicleInfo.isModified === 'Yes',
        bidding_duration: parseInt(formData.biddingDuration),
        start_date: formData.startDate,
        expires_at: expiryDate.toISOString(),
        created_at: now.toISOString(),
        updated_at: now.toISOString(),
        
        // Coverage Information
        no_claims_years: parseInt(formData.coverageInfo.noClaimsYears),
        coverage_type: formData.coverageInfo.coverageType,
        breakdown_cover: formData.coverageInfo.additionalCover.includes('Breakdown Cover'),
        legal_protection: formData.coverageInfo.additionalCover.includes('Legal Protection'),
        windscreen_cover: formData.coverageInfo.additionalCover.includes('Windscreen Cover'),
        courtesy_car: formData.coverageInfo.additionalCover.includes('Courtesy Car'),
        payment_frequency: formData.coverageInfo.paymentFrequency,
        had_insurance_before: formData.coverageInfo.hadInsuranceBefore === 'Yes',
        protect_no_claims: formData.coverageInfo.protectNoClaims,

        // Driver Information
        marital_status: formData.driverInfo.maritalStatus,
        is_home_owner: formData.driverInfo.isHomeOwner === 'Yes',
        lived_in_uk_since_birth: formData.driverInfo.livedInUKSinceBirth === 'Yes',
        employment_status: formData.driverInfo.employmentStatus,
        license_type: formData.driverInfo.licenseType,
        license_held_years: parseInt(formData.driverInfo.licenseHeld.years),
        license_held_months: parseInt(formData.driverInfo.licenseHeld.years) === 0 ? 
          parseInt(formData.driverInfo.licenseHeld.months || '0') : null,
        other_vehicles: formData.driverInfo.otherVehicles
      };

      // Validate required fields
      const requiredFields = [
        'age', 'email', 'name', 'car_make', 'car_model', 'registration',
        'gender', 'date_of_birth', 'address', 'region', 'car_year', 'mileage', 'start_date',
        'no_claims_years', 'coverage_type', 'marital_status', 'employment_status',
        'license_type', 'license_held_years'
      ];

      for (const field of requiredFields) {
        if (!requestData[field]) {
          throw new Error(`Missing required field: ${field}`);
        }
      }

      // Validate numeric fields
      if (isNaN(requestData.age) || requestData.age < 17) {
        throw new Error('Invalid age - must be at least 17');
      }

      // Validate start date
      const startDate = new Date(requestData.start_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (startDate < today) {
        throw new Error('Start date cannot be in the past');
      }
      
      // Validate other numeric fields
      const numericFields = {
        car_year: { min: 1900, max: new Date().getFullYear() },
        mileage: { min: 0 },
        no_claims_years: { min: 0 },
        license_held_years: { min: 0 }
      };

      for (const [field, range] of Object.entries(numericFields)) {
        const value = requestData[field as keyof typeof requestData];
        if (isNaN(value) || value < range.min || ('max' in range && value > range.max)) {
          throw new Error(`Invalid value for ${field}`);
        }
      }

      // Validate months only if years is 0
      if (requestData.license_held_years === 0) {
        const months = requestData.license_held_months;
        if (months === null || isNaN(months) || months < 0 || months > 11) {
          throw new Error('Invalid license held months');
        }
      }
      // Submit the request
      const { data, error: submitError } = await supabase
        .from('insurance_requests')
        .insert([requestData])
        .select()
        .single();

      if (submitError?.code === '23514') {
        // Handle age constraint violation specifically
        throw new Error('Age validation failed - you must be at least 17 years old');
      }

      if (submitError) throw submitError;

      // If vehicle has modifications, add them
      if (formData.vehicleInfo.isModified === 'Yes' && formData.vehicleInfo.modifications.length > 0) {
        const { error: modError } = await supabase
          .from('vehicle_modifications')
          .insert(
            formData.vehicleInfo.modifications.map(mod => ({
              request_id: data[0].id,
              modification_type_id: mod.type_id,
              details: mod.details
            }))
          );

        if (modError) throw modError;
      }

      onSuccess?.();
      return { success: true };
    } catch (err) {
      console.error('Form submission error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to submit quote request';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsSubmitting(false);
    }
  };

  return { handleSubmit, isSubmitting, error };
}

// Generate a unique reference number
function generateReferenceNumber(): string {
  // Generate a numeric reference number
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 10000);
  return `${timestamp}${random}`;
}

function calculateAge(dob: string): number {
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return age;
}